import React, { useContext } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthContext } from "./utils/AuthContext";
import { Container } from "styles/global-style";

// Components
import MainMenu from "./components/MainMenu";
import AuthMainMenu from "./components/Menus/AuthMainMenu";
import AuthSettingsMenu from "components/Menus/AuthSettingsMenu";
import Footer from "./components/Footer";
import TrialNudge from "components/Trial/TrialNudge";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Join from "./pages/Join";
import Forgot from "./pages/Forgot";
import Password from "./pages/Password";

// Auth pages
import Work from "pages/Auth/Work";
import Timeline from "pages/Auth/Timeline";
import SetOrgAndProj from "pages/Auth/SetOrgAndProj";
import WorkTree from "pages/Auth/WorkTree";
import Project from "pages/Auth/Project";
import Profile from "pages/Auth/Profile";

// Auth/Settings
import UserAccount from "./pages/Auth/Settings/Account";
import Organization from "./pages/Auth/Settings/Organization";
import ProjectSettings from "./pages/Auth/Settings/ProjectSettings";
import Analytics from "./pages/Auth/Settings/Analytics";
import DoDs from "./pages/Auth/Settings/DoDs";
import Disciplines from "./pages/Auth/Settings/Disciplines";
import Import from "./pages/Auth/Settings/Import";
import Export from "./pages/Auth/Settings/Export";
import Users from "./pages/Auth/Settings/Users";
import General from "./pages/Auth/Settings/General";

const isProjectSettingEnabled = (user, settingName, defaultValue = true) => {
  const rawValue = user?.proj?.settings?.find((s) => s.name === settingName)?.value;
  if (rawValue === undefined || rawValue === null) return defaultValue;
  return rawValue === true || rawValue === "true";
};

function App() {
  return (
    <Routes>
      <Route element={<Root />}>
        <Route index element={<Home />} />
        <Route path="/" element={<Home />} />
        <Route path="login" element={<Login title="Login" />} />
        <Route path="join" element={<Join title="Join" />} />
        <Route path="forgot" element={<Forgot title="Forgot" />} />
      </Route>
      <Route element={<RootPassword />}>
        <Route path="password" element={<Password />} />
      </Route>
      <Route path="auth" element={<Auth />}>
        <Route index element={<Navigate to="timeline" replace />} />
        <Route path="orgproj" element={<SetOrgAndProj />} />
        <Route
          path="work"
          element={
            <RequireTabEnabled settingName="workTabEnabled">
              <Work />
            </RequireTabEnabled>
          }
        />
        <Route path="timeline" element={<Timeline />} />
        <Route path="tree" element={<WorkTree />} />
        <Route
          path="project"
          element={
            <RequireTabEnabled settingName="projectTabEnabled">
              <Project />
            </RequireTabEnabled>
          }
        />
        <Route
          path="profile/:personId"
          element={
            <RequireRealAccount feature="profile">
              <Profile />
            </RequireRealAccount>
          }
        />
        <Route path="settings" element={<Settings />}>
          <Route index element={<SettingsIndexRedirect />} />
          <Route
            path="general"
            element={
              <RequireRole minimumRole="admin">
                <General />
              </RequireRole>
            }
          />
          <Route path="account" element={<UserAccount />} />
          <Route
            path="org"
            element={
              <RequireRealAccount feature="organization">
                <Organization />
              </RequireRealAccount>
            }
          />
          <Route path="proj" element={<ProjectSettings />} />
          <Route
            path="users"
            element={
              <RequireRealAccount feature="users">
                <Users />
              </RequireRealAccount>
            }
          />
          <Route path="disciplines" element={<Disciplines />} />
          <Route
            path="dods"
            element={
              <RequireRealAccount feature="dods">
                <DoDs />
              </RequireRealAccount>
            }
          />
          <Route
            path="export"
            element={
              <RequireRealAccount feature="export">
                <Export />
              </RequireRealAccount>
            }
          />
          <Route
            path="import"
            element={
              <RequireRealAccount feature="import">
                <Import />
              </RequireRealAccount>
            }
          />
          <Route
            path="analytics"
            element={
              <RequireRealAccount feature="analytics">
                <Analytics />
              </RequireRealAccount>
            }
          />
          <Route path="password" element={<Password />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Root() {
  const { user, isTrial } = useContext(AuthContext);

  // Trial users are technically "users" but they shouldn't be bounced away
  // from public routes (/, /login, /join, /forgot). The plain `Root` is also
  // used for /, so check the pathname to decide whether to redirect.
  const location = useLocation();
  const publicPaths = ["/login", "/join", "/forgot"];
  if (user && !isTrial) {
    return <Navigate to="/auth" />;
  }
  if (user && isTrial && !publicPaths.includes(location.pathname)) {
    // Trial user landing on `/` should jump straight into the timeline.
    return <Navigate to="/auth/timeline" replace />;
  }
  return (
    <>
      <MainMenu />
      <div className="container">
        <Outlet />
      </div>
      <Footer />
    </>
  );
}

function RootPassword() {
  return (
    <>
      <MainMenu />
      <div className="container">
        <Outlet />
      </div>
      <Footer />
    </>
  );
}

function Auth() {
  const { user, setUserMembership, isTrial } = useContext(AuthContext);
  let location = useLocation();

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  else if (!user.org && !location.pathname.includes("orgproj")) return <Navigate to="/auth/orgproj" state={{ from: location }} replace />;

  // If invited, flip status to active person's in org membership. Skip for
  // trial since there are no real memberships to update.
  if (!isTrial) {
    const currentMembership = user.memberships?.find((mem) => mem.org_id === user.current_org_id);
    if (currentMembership?.status === "invited") setUserMembership("active", currentMembership.id);
  }

  return (
    <>
      <AuthMainMenu />
      <Outlet />
    </>
  );
}

function Settings() {
  return (
    <Container>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ width: 200 }}>
          <AuthSettingsMenu />
        </div>
        <div style={{ width: 650 }}>
          <Outlet />
        </div>
      </div>
    </Container>
  );
}

function RequireTabEnabled({ settingName, children }) {
  const { user } = useContext(AuthContext);
  const isEnabled = isProjectSettingEnabled(user, settingName);
  if (!isEnabled) return <Navigate to="/auth/timeline" replace />;
  return children;
}

function RequireRole({ minimumRole, children }) {
  const { roleAccess } = useContext(AuthContext);
  if (!roleAccess(minimumRole)) return <Navigate to="/auth/settings/account" replace />;
  return children;
}

// Render a "Sign up to save" panel in place of pages that materially require
// a real account (multi-user, invites, analytics, etc.) while in trial mode.
function RequireRealAccount({ children }) {
  const { isTrial } = useContext(AuthContext);
  if (isTrial) {
    return <TrialNudge mode="panel" />;
  }
  return children;
}

function SettingsIndexRedirect() {
  const { roleAccess } = useContext(AuthContext);
  return <Navigate to={roleAccess("admin") ? "general" : "account"} replace />;
}

export default App;
